import { cn } from "@/lib/utils";
import { round1 } from "@/bizminer/lib/heatmap/scoreMath";
import {
  type HeatmapTileStatus,
  tileBgColor,
} from "@/bizminer/lib/heatmap/tileColors";

interface HeatmapGroupCardProps {
  name: string;
  companyScore: number | null;
  peersScore: number | null;
  status: HeatmapTileStatus;
  rowSpan: number;
  companyLabel: string;
  peersLabel: string;
}

export default function HeatmapGroupCard({
  name,
  companyScore,
  peersScore,
  status,
  rowSpan,
  companyLabel,
  peersLabel,
}: HeatmapGroupCardProps) {
  // Same styling as HeatmapTileButton - hardcoded slate-900 for contrast
  const companyValue = Number.isFinite(companyScore)
    ? round1(companyScore as number).toFixed(1)
    : "—";
  const peersValue = Number.isFinite(peersScore)
    ? round1(peersScore as number).toFixed(1)
    : "—";
  const isNoData = status === "no_data";

  return (
    <div
      className={cn(
        "rounded-lg border shadow-sm overflow-hidden",
        // Same text/border colors as regular tiles
        isNoData
          ? "border-border/60 text-foreground"
          : "border-slate-900/10 text-slate-900",
        // Taller height for multi-row span
        "grid grid-rows-[auto_1fr]",
        // Subtle hover effect for visual consistency
        "transition-all duration-200",
        "hover:shadow-md hover:scale-[1.02]",
      )}
      style={{
        backgroundColor: tileBgColor(status),
        height: rowSpan === 2 ? "272px" : "130px",
      }}
      aria-label={`${name}: ${companyLabel} ${companyValue}, ${peersLabel} ${peersValue}`}
    >
      {/* Title */}
      <div className="px-4 pt-4">
        <div className="text-lg font-extrabold leading-tight">
          {name}
        </div>
      </div>

      {/* Scores */}
      <div className="px-4 pb-4 flex items-end">
        <div className="w-full border-t border-[color:var(--bmad-score-divider)] pt-4">
          <div className="grid grid-cols-2 gap-6">
            <div className="grid gap-1">
              <div className="text-[10px] font-semibold uppercase tracking-[0.08em] opacity-70">
                {companyLabel}
              </div>
              <div className="text-2xl font-extrabold tabular-nums leading-none">
                {companyValue}
              </div>
            </div>
            <div className="grid gap-1">
              <div className="text-[10px] font-semibold uppercase tracking-[0.08em] opacity-70">
                {peersLabel}
              </div>
              <div className="text-2xl font-extrabold tabular-nums leading-none">
                {peersValue}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
